import { Platform } from '@angular/cdk/platform';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { DashboardPreference } from '@onviobr/blue-dashboard';
import { sessionNgProvider } from '@onviobr/security-services';
import { has as hasAccess, not as hasNoAccess } from '@onviobr/services/access';
import { has as hasLicense } from '@onviobr/services/licenses';
import { has } from '@onviobr/services/permission';
import { addPreference, getPreference, patchPreference } from '@onviobr/services/preference';
import { getCompanyId } from '@onviobr/services/session';
import { AuTaxLodgePerformanceService } from 'autax-staff';

@Component({
	selector: 'app-staff-dashboard',
	templateUrl: './staff-dashboard.component.html',
	styleUrls: ['./staff-dashboard.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [sessionNgProvider],
})
export class StaffDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() wizardDefinition: any;
	@Input() wizardLandingPageDefinition: any;
	@Input() wizardTranslationsLoaded: boolean;
	@Input() background: string;
	@Output() wizardOpenModal = new EventEmitter<{
		value: any;
		sectionId: any;
	}>();
	@Output() dashboardContentInitialized = new EventEmitter<any>();
	@Output() dashboardWidgetIsVisible = new EventEmitter<any>();
	@Output() dashboardSectionIsVisible = new EventEmitter<any>();

	public isLoadingDashboardLayout = true;
	public dashboardStyle = { opacity: 0, transition: '' };
	public dashboardStyle2 = { backgroundColor: '#ffffff', transition: '' };

	public preferences: DashboardPreference;
	public preferenceId: string;

	public preference = {
		getPreference,
		patchPreference,
		addPreference,
	};
	public licenses = hasLicense;
	public hasPermission = has;
	public hasAccess = hasAccess;
	public hasNoAccess = hasNoAccess;
	public getCompanyId = getCompanyId;

	hasSetupPermission = false;
	hasTrialPermission = false;
	hasPreviewPermission = false;
	hasMyAssignmentsPermission = false;
	hasTimeRecapPermission = false;
	hasSetupAssistantAccess = false;
	isLoading = true;
	isEditable = false;
	isEditMode = false;
	hasBrazilAccess = false;
	initialized = false;
	hasAUPermission = false;
	hasOtherApplications = false;
	showLDGPRGMWidget = false;
	isSaffronWidget = false;

	constructor(
		platformService: Platform,
		public lodgeProgramService: AuTaxLodgePerformanceService
	) {
		this.isEditable = !(platformService.TRIDENT || platformService.IOS);
	}

	ngAfterViewInit(): void {
		try {
			const observerr = new MutationObserver((_, observer) => {
				const divElements = document.querySelectorAll('div[qe-id="nav-link-edit-visible"]');
				if (divElements.length > 0) {
					divElements.forEach((element) => {
						element?.setAttribute('role', 'button');
						element?.parentElement?.setAttribute('role', 'presentation');
					});
					observer.disconnect();
				}
			});
			observerr.observe(document.body, { childList: true, subtree: true });
		} catch (error) {
			console.error('Error observing mutations:', error);
		}
	}

	async ngOnInit() {
		try {
			const preferences = await this.preference.getPreference(
				'onvio',
				'staffDashboard'
			);
			this.isSaffronWidget = await this.hasNoAccess('CoreDisableEfileDashboardWidget');
			this.hasBrazilAccess = await this.hasAccess('BR_DisableSetupOptions');
			this.hasSetupPermission = await this.hasPermission('Platform.SetupGuide');
			this.hasTrialPermission = await this.hasPermission('Platform.Trial');
			this.hasPreviewPermission = await this.hasPermission(
				'Platform.PreviewMode'
			);

			// Removed Argentina-specific access check

			this.hasMyAssignmentsPermission =
				(await this.hasPermission('OnvioSetup.Projects')) &&
				!this.hasBrazilAccess;
			this.hasTimeRecapPermission = await this.hasPermission(
				'TimeAndBilling.TimeAndExpense'
			);
			this.hasSetupAssistantAccess =
				(await this.hasAccess('Core_Hide_Setup_Assistant')) ||
				(await this.licenses('OnvioUS.EssentialsTrial')) || (await this.hasAccess('HideSetupAssistant'));
			this.hasAUPermission = await this.hasPermission('Applications.AUTax');
			this.hasOtherApplications =
				(await this.hasPermission('Applications.K1Analyzer') || await this.hasPermission('UltraTaxCS.UTPLicense'));

			if (preferences.length > 0) {
				this.preferenceId = preferences[0].id;
				this.preferences = preferences[0].value as DashboardPreference;
			}

			this.isLoading = false;
			this.initialized = true;

			if (!this.hasSetupAssistantAccess && this.wizardTranslationsLoaded) {
				this.isEditMode = false;
			}

			if (this.hasAUPermission) {
				const companyId = await this.getCompanyId();
				const showWidget = await this.lodgeProgramService.showHideWidget(
					companyId
				);
				this.showLDGPRGMWidget = showWidget ? true : false;
			} else {
				this.showLDGPRGMWidget = false;
			}
		} catch (error) {
			this.isLoading = false;
			this.initialized = false;
			console.warn(
				'Error trying to get Access and Permissions for Staff Dashboard'
			);
		}
		setTimeout(() => this._completeLoadingDashboard(), 2000);
	}

	// After the dashboard layout has loaded and should have manipulated the DOM, display it softly
	_completeLoadingDashboard(): void {
		this.isLoadingDashboardLayout = false;
		this.dashboardStyle = { opacity: 1, transition: 'opacity 2s' };
		this.dashboardStyle2 = {
			backgroundColor: 'transparent',
			transition: 'background-color 2s',
		};
	}

	public onEditModeChange(isEditMode: boolean) {
		this.isEditMode = isEditMode;
	}

	public onContentInitialized(data: any) {
		this.createMFEs();

		this.dashboardContentInitialized.emit(data);
	}

	public onSectionIsVisible(data: any) {
		this.dashboardSectionIsVisible.emit(data);
	}

	public onWidgetIsVisible(data: any) {
		this.dashboardWidgetIsVisible.emit(data);
	}

	public async onSave(data: DashboardPreference) {
		try {
			if (this.preferenceId) {
				await this.preference.patchPreference(data, this.preferenceId);
			} else {
				await this.preference.addPreference({
					application: 'onvio',
					category: 'staffDashboard',
					value: data,
				});
			}
		} catch (error) {
			console.error(error);
		}
	}

	public createMFEs(_destroyNode = false) {
		// Removed Argentina-specific widgets - no longer using @onvioar modules
		// Only BR/US functionality remains
	}

	public createMFENode(
		moduleName: string,
		widgetId: string,
		enableBento = false,
		destroyNode = false
	) {
		let mfeNode;
		const widget = document.getElementById(widgetId);

		if (widget) {
			const mfe = widget.getElementsByTagName('mfe-loader');
			const widgetContent = widget.getElementsByClassName('card-body');

			Array.from(mfe).forEach((module) => {
				mfeNode = module;
			});

			if (!mfeNode) {
				mfeNode = document.createElement('mfe-loader');
				mfeNode.setAttribute('module-name', moduleName);

				if (enableBento) {
					mfeNode.classList.add('ng-bento');
				}
			}

			if (widgetContent) {
				if (mfeNode) {
					(widgetContent[0] as HTMLElement).appendChild(mfeNode);

					mfeNode.preventUnmount = true;

					// It's important to destroy MFE node
					if (destroyNode) {
						mfeNode.preventUnmount = false;
						mfeNode = undefined;
					}
				}
			}
		}
	}

	ngOnDestroy(): void {
		this.createMFEs(true);
	}
}
